<script lang="ts" setup>
import type { HeadingVariants, SizeVariants } from '~kui/types/kui';

interface Props {
  background?: string;
  /**
   * 'inherit' emits no font-size of its own, so the highlight matches whatever text
   * wraps it. Required when highlighting a phrase inside a heading: the fixed steps
   * below do not follow a design-system `Heading`'s responsive ramp, so picking
   * the nearest step leaves the highlight a different size from the heading.
   */
  fontSize?: Extract<
    SizeVariants,
    'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '5xl' | '4xl' | '6xl'
  > | Extract<HeadingVariants, 'h1' | 'h2' | 'h4'> | 'inherit';
  inline?: boolean; // more compact version, eg. highlighting text within a sentence
  // The blurred marker glow reads well on flat page backgrounds but smears into a haze
  // over photos and gradients — 'solid' renders one crisp rounded box instead.
  appearance?: 'glow' | 'solid';
}

const props = withDefaults(defineProps<Props>(), {
  background: 'bg-white',
  fontSize: 'md',
  inline: false,
  appearance: 'glow',
});

const fontSizeClasses = computed(() => {
  return twMerge(
    cva('', {
      variants: {
        fontSize: {
          'inherit': '',
          'sm': 'text-sm',
          'md': 'text-base',
          'lg': 'text-lg',
          'xl': 'text-xl',
          '2xl': 'text-2xl',
          '3xl': 'text-3xl',
          '4xl': 'text-2xl md:text-4xl',
          '5xl': 'text-5xl',
          '6xl': 'text-5xl md:text-6xl',
          'h1': 'text-5xl md:text-6xl',
          'h2': 'text-4xl',
          'h4': 'text-xl',
        },
        inline: {
          true: '',
          false: 'py-2',
        },
      },
    })({
      fontSize: props.fontSize,
    }),
  );
});
</script>

<template>
  <span v-if="appearance === 'solid'" class="relative z-1 inline-block">
    <span
      class="inline-block rounded-lg px-3 py-2 whitespace-pre-wrap"
      :class="[background, fontSizeClasses]"
    >
      <slot name="text" />
      <slot name="post" />
    </span>
  </span>
  <span v-else class="relative inline-block z-1">
    <!--
    There are two blurred backgrounds since the blur-sm is too transparent but covers the text
    while blur-xs is stronger but does not fully cover the text. So we neeed both.
    Also duplicated for slightly left and slightly right as adding padding changesthe text wrapping.
    -->
    <span class="absolute inset-0 -left-2 mr-2">
      <span
        class="whitespace-pre-wrap text-transparent blur-xs inline"
        :class="[background, fontSizeClasses, { 'py-2': !inline }]"
        aria-hidden="true"
        role="presentation"
      >
        <slot name="text" />
      </span>
    </span>
    <span v-if="!inline" class="absolute inset-0 -left-2 mr-2">
      <span
        class="whitespace-pre-wrap text-transparent blur-sm inline"
        :class="[background, fontSizeClasses]"
        aria-hidden="true"
        role="presentation"
      >
        <slot name="text" />
      </span>
    </span>
    <span class="absolute inset-0 left-2 -mr-2">
      <span
        class="whitespace-pre-wrap text-transparent blur-xs inline"
        :class="[background, fontSizeClasses, { 'py-2': !inline }]"
        aria-hidden="true"
        role="presentation"
      >
        <slot name="text" />
      </span>
    </span>
    <span v-if="!inline" class="absolute inset-0 left-2 -mr-2">
      <span
        class="whitespace-pre-wrap text-transparent blur-sm inline"
        :class="[background, fontSizeClasses]"
        aria-hidden="true"
        role="presentation"
      >
        <slot name="text" />
      </span>
    </span>
    <!--
    The text needs to be place over the top of the blurred background since blur-* also blurs the text.
    -->

    <span
      class="pointer-events-none relative z-10 whitespace-pre-wrap py-1"
      :class="fontSizeClasses"
    >
      <slot name="text" />
      <slot name="post" />
    </span>
  </span>
</template>
